import { Link } from 'react-router-dom';
import { Sparkline } from './Sparkline';

const GUIDE_URL = 'https://zafron.dev/docs/mcp/dashboards';

const PRIMARY =
  'inline-flex items-center rounded-lg bg-gray-900 px-5 py-3 font-medium text-white ' +
  'hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900';
const SECONDARY =
  'inline-flex items-center rounded-lg border border-gray-200 bg-white px-5 py-3 font-medium text-gray-900 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-50 ' +
  'hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 dark:hover:bg-gray-800 dark:focus-visible:ring-offset-gray-900';

/**
 * The top of the lander, shown whenever there are no dashboards to list.
 * `noDevices` swaps the guide for "Add a device": connecting an assistant to
 * an account with nothing to chart is a dead end.
 */
export function DashboardsHero({ noDevices = false }) {
  return (
    <section className="grid items-center gap-12 lg:grid-cols-2">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700 dark:text-blue-400">
          Dashboards
        </p>
        <h1 className="mt-4 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl dark:text-gray-50">
          Describe the dashboard.
          <br />
          Your assistant builds it.
        </h1>
        <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-600 dark:text-gray-400">
          Connect Claude or any MCP client to Zafron and ask for what you want to
          see. It reads your devices, writes the dashboard, and it shows up here
          with live data.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#connect" className={PRIMARY}>Connect your assistant</a>
          {noDevices ? (
            <Link to="/devices" className={SECONDARY}>Add a device</Link>
          ) : (
            <a href={GUIDE_URL} target="_blank" rel="noopener noreferrer" className={SECONDARY}>
              Read the guide<span className="sr-only"> (opens in a new tab)</span>
            </a>
          )}
        </div>
      </div>

      <Illustration />
    </section>
  );
}

const TEMP = [
  20.1, 21.4, 20.6, 19.8, 20.3, 19.2, 18.9, 19.6, 18.7, 18.2, 18.8, 18.1, 17.9,
  18.4, 18.0, 18.6, 18.3, 19.1, 18.8, 19.9, 19.4, 20.8, 21.9, 21.2, 23.8, 25.1,
  24.3, 26.6, 25.8, 27.9, 27.1, 28.4, 26.9, 28.8, 27.6, 28.3, 26.8, 28.6, 27.2,
  26.4, 27.8, 26.1, 27.3,
];

function Illustration() {
  return (
    <div aria-hidden="true" className="relative pb-20">
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-gray-300/40 dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center gap-1.5 border-b border-gray-100 bg-gray-50 px-4 py-2.5 dark:border-gray-800 dark:bg-gray-800">
          <span className="h-2.5 w-2.5 rounded-full bg-gray-300 dark:bg-gray-600" />
          <span className="h-2.5 w-2.5 rounded-full bg-gray-300 dark:bg-gray-600" />
          <span className="h-2.5 w-2.5 rounded-full bg-gray-300 dark:bg-gray-600" />
          <span className="ml-2 font-mono text-xs text-gray-500 dark:text-gray-400">Farm Monitor</span>
        </div>
        <div className="space-y-4 p-5">
          <div className="grid grid-cols-3 gap-3">
            {[['Temperature', '28.0 °C'], ['Humidity', '47.4 %'], ['Battery', '2.81 V']].map(
              ([label, value]) => (
                <div key={label} className="rounded-lg bg-gray-100/70 px-3 py-2.5 dark:bg-gray-800/70">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                  <p className="mt-0.5 text-lg font-semibold text-gray-900 sm:text-xl dark:text-gray-50">{value}</p>
                </div>
              )
            )}
          </div>
          <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-800">
            <p className="text-xs font-semibold text-gray-900 dark:text-gray-50">Temperature · 24 h</p>
            <Sparkline
              data={TEMP}
              color="#f97316"
              fill="#fff1e6"
              fillClassName="text-[#fff1e6] dark:text-orange-950/40"
              className="mt-3 h-28 w-full"
            />
          </div>
        </div>
      </div>

      <div className="absolute bottom-16 left-0 max-w-xs rounded-xl bg-gray-900 px-4 py-3 text-sm text-white shadow-lg lg:-translate-x-8">
        Make a farm dashboard with temperature, humidity and battery for my LHT65.
      </div>
      <div className="absolute bottom-2 left-0 flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm lg:-translate-x-8 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300">
        <span className="h-2 w-2 rounded-full bg-green-500" />
        Saved &ldquo;Farm Monitor&rdquo; to Zafron · 4 widgets
      </div>
    </div>
  );
}
